import { StorageTypeEnum } from '@/common/constants/parties.constants';
import { Load } from '@/common/models/ILoads';
import useStore from '@/lib/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { analysisSchema } from '../schemas/analysis.schema';
import { useLoadStore } from '../store';

export interface FarmerByTaxnumber {
  id: string;
  number: string;
  farmer_tax_number: string;
  cluster_tax_number: string | null;
  weight: string | null;
}

type FormValues = {
  humidity: string;
  pollution: string;
  natura: string;
  grain_mixture: string;
  kleykovina: string;
  steklovidnost: string;
  grain_class: string;
  prime_grain?: string;
  farmer_contract_id: string;
  selected_date?: string;
};

type SaveGrainAnalysisPayload = {
  ids: string[];
  grain_class: number;
  humidity: number;
  pollution: number;
  grain_mixture: number;
  natura: number;
  kleykovina: number;
  steklovidnost: number;
  farmer_contract_id: string;
  prime_grain?: number;
};

export const useAnalysisByGrain = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useStore();
  const navigate = useNavigate();
  const suggestionRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedChips, setSelectedChips] = useState<Load[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [contractList, setContractList] = useState<any[]>([]);

  const {
    loadGrain,
    farmerByTaxnumbers,
    fetchGrainDataDetail,
    fetchAnalysisByGrainTaxnumbers,
    saveGrainAnalysis,
    grainAnalysisLoading,
    fetchFarmerGrainPhysical,
  } = useLoadStore();

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: (values, context, options) =>
      yupResolver(analysisSchema)(
        values,
        {
          ...context,
          isSeed:
            contractList.find((el) => el.value === values.farmer_contract_id)
              ?.contract_type === StorageTypeEnum.SEED,
        },
        options,
      ),
    defaultValues: {
      humidity: '',
      pollution: '',
      natura: '',
      grain_mixture: '',
      kleykovina: '',
      steklovidnost: '',
      grain_class: '',
      prime_grain: '',
      farmer_contract_id: '',
      selected_date: '',
    },
  });

  const farmerContractId = useWatch({
    control: form.control,
    name: 'farmer_contract_id',
  });

  const selectedDate = useWatch({
    control: form.control,
    name: 'selected_date',
  });

  const isSeed =
    contractList.find((el) => el.value === farmerContractId)?.contract_type ===
    StorageTypeEnum.SEED;

  useEffect(() => {
    if (form.formState.isSubmitted) {
      form.trigger();
    }
  }, [isSeed]);

  const getFarmer = async () => {
    try {
      const farmer = await fetchFarmerGrainPhysical!(
        loadGrain?.farmer_tax_number as any,
      );
      setContractList(
        farmer?.map((el: any) => ({
          label: el?.contract_number,
          value: el.id,
          ...el,
        })),
      );
    } catch (error) {
      console.error('Failed to fetch farmer:', error);
    }
  };

  useEffect(() => {
    if (loadGrain?.farmer_tax_number) getFarmer();
  }, [loadGrain?.farmer_tax_number]);

  useEffect(() => {
    if (id) {
      fetchGrainDataDetail(id);
    }
  }, []);

  useEffect(() => {
    if (loadGrain?.farmer_tax_number && farmerContractId) {
      fetchAnalysisByGrainTaxnumbers(loadGrain?.farmer_tax_number, {
        farmer_contract_id: farmerContractId,
        selected_date: selectedDate
          ? new Date(selectedDate!)?.toISOString()
          : undefined,
      });
    }
  }, [
    loadGrain?.farmer_tax_number,
    farmerContractId,
    fetchAnalysisByGrainTaxnumbers,
    selectedDate,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchValue.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [searchValue]);

  const toggleChip = (item: FarmerByTaxnumber): void => {
    setSelectedChips((prev) =>
      prev.some((el) => el.id === item.id)
        ? prev.filter((el) => el.id !== item.id)
        : [...prev, item],
    );
  };

  const selectAllChips = (items: FarmerByTaxnumber[]): void => {
    setSelectedChips(items);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (selectedChips.length === 0) {
      toast.error('Kamida bitta yukni tanlang');
      return;
    }

    try {
      const payload: SaveGrainAnalysisPayload = {
        ids: selectedChips.map((el) => el.id),
        grain_class: Number(data.grain_class),
        humidity: Number(data.humidity),
        pollution: Number(data.pollution),
        grain_mixture: Number(data.grain_mixture),
        natura: Number(data.natura),
        kleykovina: Number(data.kleykovina),
        steklovidnost: Number(data.steklovidnost),
        farmer_contract_id: data.farmer_contract_id,
        ...(isSeed && { prime_grain: Number(data.prime_grain) }),
      };

      await saveGrainAnalysis(payload as any);
      navigate(-1);
      toast.success('Laboratoriya tahlili muvaffaqiyatli saqlandi');
    } catch (error) {
      console.error(error);
      toast.error('Xatolik yuz berdi');
    }
  };

  return {
    form,
    user,
    isSeed,
    onSubmit,
    loadGrain,
    toggleChip,
    selectAllChips,
    isDisabled: selectedChips.length === 0,
    contractList,
    selectedChips,
    suggestionRef,
    farmerContractId,
    farmerByTaxnumbers,
    grainAnalysisLoading,
  };
};
